import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../redux/authSlice";
import SendBlastModal from "./SendBlastModal";
import BlastHistoryModal from "./BlastHistoryModal";
import ScheduleTournamentModal from "./ScheduleTournamentModal";
import "./SideMenu.css";

// Left "expandable list" drawer. Opened by the top-left hamburger in AppLayout.
// Slides left->right, full viewport height. Holds the menu entries that used to be
// reachable elsewhere (Settings + Leaderboard for now); each navigates to its existing
// route and closes the drawer. Admins additionally get an "Admin controls" entry that
// swaps the list in place for the admin actions (send blast / blast history) — the
// entry is cosmetic gating only; every admin endpoint re-checks server-side.
const SideMenu = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdmin = useSelector((state) => state.notifications.isAdmin);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [view, setView] = useState("main"); // 'main' | 'admin'
  const [adminModal, setAdminModal] = useState(null); // null | 'send' | 'history'

  // A reopened drawer always starts on the main list.
  useEffect(() => {
    if (!open) setView("main");
  }, [open]);

  const go = (path) => {
    onClose();
    navigate(path);
  };

  // Logging out: drop the JWT (Redux + localStorage via the logout reducer), which also
  // tells socketMiddleware to deactivate the STOMP client. Then send the user back to the
  // login route; ProtectedRoute would redirect anyway once the token is gone.
  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    onClose();
    dispatch(logout());
    navigate("/");
  };

  return (
    <>
      <aside className={`side-menu ${open ? "open" : ""}`} aria-hidden={!open}>
        <div className="side-menu-header">
          <span className="side-menu-title">{view === "admin" ? "Admin controls" : "Menu"}</span>
          <button className="side-menu-close" onClick={onClose} aria-label="Close menu">
            &times;
          </button>
        </div>
        {view === "main" ? (
          <nav className="side-menu-list">
            <button className="side-menu-item" onClick={() => go("/settings")}>
              <span className="side-menu-item-icon">&#9881;</span> Settings
            </button>
            <button className="side-menu-item" onClick={() => go("/customize")}>
              <span className="side-menu-item-icon">&#9998;</span> Customize
            </button>
            <button className="side-menu-item" onClick={() => go("/leaderboard")}>
              <span className="side-menu-item-icon">&#9733;</span> Leaderboard
            </button>
            {isAdmin && (
              <button className="side-menu-item" onClick={() => setView("admin")}>
                <span className="side-menu-item-icon">&#9878;</span> Admin controls
              </button>
            )}
            <button
              className="side-menu-item side-menu-item-logout"
              onClick={() => setShowLogoutConfirm(true)}
            >
              <span className="side-menu-item-icon">&#9099;</span> Logout
            </button>
          </nav>
        ) : (
          <nav className="side-menu-list">
            <button className="side-menu-item" onClick={() => setView("main")}>
              <span className="side-menu-item-icon">&#8592;</span> Menu
            </button>
            <button className="side-menu-item" onClick={() => setAdminModal("send")}>
              <span className="side-menu-item-icon">&#128227;</span> Send notification blast
            </button>
            <button className="side-menu-item" onClick={() => setAdminModal("history")}>
              <span className="side-menu-item-icon">&#128220;</span> Admin Blast History
            </button>
            <button className="side-menu-item" onClick={() => setAdminModal("tournament")}>
              <span className="side-menu-item-icon">&#127942;</span> Schedule tournament
            </button>
          </nav>
        )}
      </aside>

      {/* Rendered as a sibling of the aside (not a child) so position:fixed centers on the
          viewport — the aside's transform would otherwise become its containing block. */}
      {showLogoutConfirm && (
        <div
          className="logout-confirm-overlay"
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            className="logout-confirm-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <p className="logout-confirm-text">Are you sure you want to log out?</p>
            <div className="logout-confirm-buttons">
              <button
                className="logout-confirm-btn logout-confirm-btn-logout"
                onClick={confirmLogout}
              >
                Logout
              </button>
              <button
                className="logout-confirm-btn logout-confirm-btn-cancel"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin modals — siblings of the aside for the same containing-block reason. */}
      {adminModal === "send" && <SendBlastModal onClose={() => setAdminModal(null)} />}
      {adminModal === "history" && <BlastHistoryModal onClose={() => setAdminModal(null)} />}
      {adminModal === "tournament" && <ScheduleTournamentModal onClose={() => setAdminModal(null)} />}
    </>
  );
};

export default SideMenu;
